import { MoreVertical, Umbrella, Plus, Edit, Trash2, ListPlus } from 'lucide-react';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

interface DayCellMenuProps {
  onAddTask?: () => void;
  onAddAbsence: () => void;
  onAddWorkDay: () => void;
  onEditDay: () => void;
  onRemoveDay: () => void;
  isAbsence: boolean;
  isWorkDay: boolean;
}

const MENU_WIDTH = 192; // matches w-48
const MARGIN = 8;

export function DayCellMenu({
  onAddTask,
  onAddAbsence,
  onAddWorkDay,
  onEditDay,
  onRemoveDay,
  isAbsence,
  isWorkDay,
}: DayCellMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    let left = rect.right - MENU_WIDTH;
    if (left < MARGIN) left = MARGIN;
    if (left + MENU_WIDTH > window.innerWidth - MARGIN) {
      left = window.innerWidth - MENU_WIDTH - MARGIN;
    }
    setPos({ top: rect.bottom + 4, left });
  };

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const handleReposition = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen]);

  const menu = isOpen && pos ? (
    <div
      ref={menuRef}
      role="menu"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH }}
      className="
        bg-white dark:bg-gray-800 rounded-lg shadow-lg
        border border-gray-200 dark:border-gray-700 py-1 z-[1000]
      "
    >
      {isWorkDay && onAddTask && (
        <button
          type="button"
          onClick={() => {
            onAddTask();
            setIsOpen(false);
          }}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
        >
          <ListPlus className="w-4 h-4" />
          Aufgabe zuweisen
        </button>
      )}

      {!isWorkDay && (
        <button
          type="button"
          onClick={() => {
            onAddWorkDay();
            setIsOpen(false);
          }}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <Plus className="w-4 h-4" />
          Arbeitstag anlegen
        </button>
      )}

      {!isAbsence && (
        <button
          type="button"
          onClick={() => {
            onAddAbsence();
            setIsOpen(false);
          }}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <Umbrella className="w-4 h-4" />
          Abwesenheit eintragen
        </button>
      )}

      {(isWorkDay || isAbsence) && (
        <>
          <button
            type="button"
            onClick={() => {
              onEditDay();
              setIsOpen(false);
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Edit className="w-4 h-4" />
            Tag bearbeiten
          </button>

          <hr className="my-1 border-gray-200 dark:border-gray-700" />

          <button
            type="button"
            onClick={() => {
              onRemoveDay();
              setIsOpen(false);
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-4 h-4" />
            Tag löschen
          </button>
        </>
      )}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-label="Mehr Optionen"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>
      {menu && createPortal(menu, document.body)}
    </>
  );
}
